import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Scale, Plus, History, Eye, Trash2 } from "lucide-react";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { AdminSection } from "@/components/admin/AdminSection";
import { RichTextEditor } from "@/components/admin/cms/RichTextEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  listLegalPages,
  saveLegalPage,
  deleteLegalPage,
  listLegalVersions,
  restoreLegalVersion,
} from "@/lib/admin/cms.functions";

export const Route = createFileRoute("/_admin/admin/cms/legal")({
  component: LegalPagesAdmin,
});

type LegalRow = {
  id: string;
  slug: string;
  title: string;
  body_html: string | null;
  seo_title: string | null;
  meta_description: string | null;
  keywords: string | null;
  og_image_url: string | null;
  canonical_url: string | null;
  status: string;
  display_order: number;
  version: number;
  last_updated_at: string | null;
};

type Draft = {
  id?: string;
  slug: string;
  title: string;
  body_html: string;
  seo_title: string;
  meta_description: string;
  keywords: string;
  og_image_url: string;
  canonical_url: string;
  status: "draft" | "published";
  display_order: number;
};

const emptyDraft = (order: number): Draft => ({
  slug: "",
  title: "",
  body_html: "",
  seo_title: "",
  meta_description: "",
  keywords: "",
  og_image_url: "",
  canonical_url: "",
  status: "draft",
  display_order: order,
});

function LegalPagesAdmin() {
  const list = useServerFn(listLegalPages);
  const save = useServerFn(saveLegalPage);
  const remove = useServerFn(deleteLegalPage);
  const versionsFn = useServerFn(listLegalVersions);
  const restoreFn = useServerFn(restoreLegalVersion);
  const qc = useQueryClient();

  const [draft, setDraft] = useState<Draft | null>(null);
  const [historyFor, setHistoryFor] = useState<string | null>(null);

  const { data, isLoading } = useQuery({ queryKey: ["cms-admin", "legal"], queryFn: () => list() });
  const pages = useMemo(
    () => ((data ?? []) as unknown as LegalRow[]).sort((a, b) => a.display_order - b.display_order),
    [data],
  );

  const { data: versions } = useQuery({
    queryKey: ["cms-admin", "legal-versions", historyFor],
    queryFn: () => versionsFn({ data: { pageId: historyFor as string } }),
    enabled: Boolean(historyFor),
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cms-admin", "legal"] });
    void qc.invalidateQueries({ queryKey: ["cms"] });
  };

  const saveMutation = useMutation({
    mutationFn: (d: Draft) =>
      save({
        data: {
          ...d,
          seo_title: d.seo_title || null,
          meta_description: d.meta_description || null,
          keywords: d.keywords || null,
          og_image_url: d.og_image_url || null,
          canonical_url: d.canonical_url || null,
        } as never,
      }),
    onSuccess: () => { toast.success("Page saved"); setDraft(null); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => { toast.success("Page deleted"); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Couldn't delete the page."),
  });

  const restoreMutation = useMutation({
    mutationFn: (v: { pageId: string; versionId: string }) => restoreFn({ data: v }),
    onSuccess: () => { toast.success("Version restored"); setHistoryFor(null); refresh(); },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Restore failed"),
  });

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Legal Pages"
        description="Terms, privacy, shipping, refunds, returns and cookies. Only published pages appear on the website."
        actions={<ImportButtons moduleId="legal-pages" />}
      />
      <div className="flex justify-end">
        <Button size="sm" className="gap-1" onClick={() => setDraft(emptyDraft(pages.length))}>
          <Plus className="h-3.5 w-3.5" aria-hidden /> New page
        </Button>
      </div>

      {isLoading ? <p className="text-sm text-muted-foreground">Loading pages…</p> : null}

      <AdminSection number={1} icon={Scale} title="Policy pages" description="Each page is published at /<slug> on the public website.">
        {pages.length === 0 ? (
          <p className="text-sm text-muted-foreground">No legal pages yet.</p>
        ) : (
          <ul className="divide-y rounded-md border">
            {pages.map((p) => (
              <li key={p.id} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                <div className="min-w-48 flex-1">
                  <p className="text-sm font-medium text-foreground">{p.title}</p>
                  <p className="text-xs text-muted-foreground">/{p.slug} · v{p.version}</p>
                </div>
                <Badge variant={p.status === "published" ? "default" : "secondary"} className="text-[10px] uppercase">
                  {p.status}
                </Badge>
                <div className="ml-auto flex gap-1">
                  <Button size="sm" variant="outline" onClick={() =>
                    setDraft({
                      id: p.id,
                      slug: p.slug,
                      title: p.title,
                      body_html: p.body_html ?? "",
                      seo_title: p.seo_title ?? "",
                      meta_description: p.meta_description ?? "",
                      keywords: p.keywords ?? "",
                      og_image_url: p.og_image_url ?? "",
                      canonical_url: p.canonical_url ?? "",
                      status: p.status === "published" ? "published" : "draft",
                      display_order: p.display_order,
                    })
                  }>
                    Edit
                  </Button>
                  <Button size="icon" variant="ghost" title="Version history" onClick={() => setHistoryFor(p.id)}>
                    <History className="h-4 w-4" aria-hidden />
                  </Button>
                  <a href={`/${p.slug}`} target="_blank" rel="noopener noreferrer" title="View live page">
                    <Button size="icon" variant="ghost">
                      <Eye className="h-4 w-4" aria-hidden />
                    </Button>
                  </a>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost">
                        <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete this page?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This removes "{p.title}" permanently. This action cannot be undone.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => deleteMutation.mutate(p.id)}>
                          Delete
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </AdminSection>

      <Dialog open={draft !== null} onOpenChange={(o) => !o && setDraft(null)}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{draft?.id ? "Edit legal page" : "New legal page"}</DialogTitle>
          </DialogHeader>
          {draft ? (
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="legal-title" className="text-xs">Title</Label>
                  <Input id="legal-title" value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="legal-slug" className="text-xs">Slug</Label>
                  <Input id="legal-slug" value={draft.slug} onChange={(e) => setDraft({ ...draft, slug: e.target.value })} placeholder="privacy-policy" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Content</Label>
                <RichTextEditor value={draft.body_html} onChange={(html) => setDraft({ ...draft, body_html: html })} />
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="legal-seo" className="text-xs">SEO title</Label>
                  <Input id="legal-seo" value={draft.seo_title} onChange={(e) => setDraft({ ...draft, seo_title: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="legal-canon" className="text-xs">Canonical URL</Label>
                  <Input id="legal-canon" value={draft.canonical_url} onChange={(e) => setDraft({ ...draft, canonical_url: e.target.value })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="legal-desc" className="text-xs">Meta description</Label>
                  <Textarea id="legal-desc" rows={2} value={draft.meta_description} onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })} />
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label htmlFor="legal-keys" className="text-xs">Keywords</Label>
                  <Input id="legal-keys" value={draft.keywords} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} />
                </div>
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDraft(null)}>Cancel</Button>
            <Button
              variant="secondary"
              disabled={saveMutation.isPending}
              onClick={() => draft && saveMutation.mutate({ ...draft, status: "draft" })}
            >
              Save as draft
            </Button>
            <Button
              disabled={!draft?.title.trim() || !draft?.slug.trim() || saveMutation.isPending}
              onClick={() => draft && saveMutation.mutate({ ...draft, status: "published" })}
            >
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={historyFor !== null} onOpenChange={(o) => !o && setHistoryFor(null)}>
        <DialogContent className="max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Version history</DialogTitle>
          </DialogHeader>
          {((versions ?? []) as unknown as { id: string; version: number; title: string; created_at: string }[]).length === 0 ? (
            <p className="text-sm text-muted-foreground">No earlier versions saved yet.</p>
          ) : (
            <ul className="divide-y rounded-md border">
              {((versions ?? []) as unknown as { id: string; version: number; title: string; created_at: string }[]).map((v) => (
                <li key={v.id} className="flex items-center gap-3 px-3 py-2">
                  <div className="flex-1">
                    <p className="text-sm text-foreground">v{v.version} — {v.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(v.created_at).toLocaleString("en-IN")}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={restoreMutation.isPending}
                    onClick={() => historyFor && restoreMutation.mutate({ pageId: historyFor, versionId: v.id })}
                  >
                    Restore
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
